// src/components/ResponsiveSidebar.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Collapse,
  ButtonBase,
  Fab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Chip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import PetsRoundedIcon from "@mui/icons-material/PetsRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LogoutIcon from "@mui/icons-material/Logout";
import UpdateIcon from "@mui/icons-material/Update";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useUser } from "../contexts/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import signOutAndClearCache from "../utils/signOutAndClearCache";
import useFeatureFlags from "../hooks/useFeatureFlags";
import useActionQueueCounts from "../hooks/useActionQueueCounts";
import usePendingRegistrations from "../hooks/usePendingRegistrations";
import {
  openSupportChat,
  SUPPORT_CHAT_UNREAD_EVENT,
} from "../constants/supportChat";
import {
  getSidebarQuickActionItems,
  getTabletMoreDrawerPaperStyles,
} from "./responsiveSidebarConfig";

const SIDEBAR_WIDTH = 254;
const APP_BAR_HEIGHT_DESKTOP = 64;

// Simple emoji icon for consistent sizing & alignment
const EmojiIcon = ({ children, selected }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      fontSize: 22,
      lineHeight: 1,
      ml: 0.1,
      transform: "translateY(1px)",
      filter: selected ? "none" : "grayscale(12%)",
    }}
  >
    {children}
  </Box>
);

// Nav groups removed from global - now dynamic inside component

// --- helpers for plan/trial display ---
function coerceDate(d) {
  if (!d) return null;
  if (typeof d?.toDate === "function") return d.toDate();
  if (d instanceof Date) return d;
  const asDate = new Date(d);
  return isNaN(asDate.getTime()) ? null : asDate;
}

function daysLeft(until) {
  const end = coerceDate(until);
  if (!end) return null;
  const ms = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function planBadge(user) {
  // prefer subscriptionStatus, then plan; default to 'trial'
  const status = (user?.subscriptionStatus || user?.plan || "trial")
    .toString()
    .toLowerCase();

  if (status === "pro" || status === "active") {
    return {
      label: "Pro",
      color: "#0ea5e9",
      bg: "rgba(14,165,233,.12)",
      tooltip: "Your subscription is active on the Pro plan.",
    };
  }

  // Trial
  const dLeft = daysLeft(user?.trialEndsAt);
  const label =
    typeof dLeft === "number"
      ? `Trial · ${dLeft} day${dLeft === 1 ? "" : "s"} left`
      : "Trial";
  const tip = (() => {
    const end = coerceDate(user?.trialEndsAt);
    if (!end) return "You're currently on a trial.";
    return `Your trial ends ${end.toLocaleDateString()} ${end.toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
      },
    )}.`;
  })();

  return {
    label,
    color: "#a16207",
    bg: "rgba(161,98,7,.12)",
    tooltip: tip,
  };
}

/**
 * `demo` switches the sidebar to the public /demo sandbox: the user comes from
 * the demo (not auth), nav targets live under demo.basePath, and nothing is
 * copied/signed-out for real. Shape: { user, currency, basePath, badge, onExit,
 * onQuickAdd }. Real app usage passes no `demo` and is unchanged.
 */
export default function ResponsiveSidebar({ children, demo }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isPhoneTouch = useMediaQuery(
    "(hover: none) and (pointer: coarse) and (max-width: 767px)",
  );
  const useBottomNavLayout = useMediaQuery(
    "(hover: none) and (pointer: coarse) and (max-width: 1366px)",
  );
  const [open, setOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [quickActionsCollapsed, setQuickActionsCollapsed] = useState(true);
  // Mirrors LiveChatWidget's unread count so the Support menu item can badge
  // it on phones, where the floating launcher (and its badge) is hidden.
  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    const onUnread = (e) => setSupportUnread(e.detail?.unread || 0);
    window.addEventListener(SUPPORT_CHAT_UNREAD_EVENT, onUnread);
    return () => window.removeEventListener(SUPPORT_CHAT_UNREAD_EVENT, onUnread);
  }, []);

  const navScrollRef = useRef(null);
  const [navOverflowHint, setNavOverflowHint] = useState({
    showTop: false,
    showBottom: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const ctx = useUser();
  const user = demo ? demo.user : ctx.user;
  const currency = demo ? demo.currency : ctx.currency;
  // Logical (real-app) path of the current page, so demo routes under
  // /demo/... highlight the same nav item as their real counterparts.
  const logicalPath = demo
    ? location.pathname.replace(/^\/demo/, "") || "/dashboard"
    : location.pathname;
  const hrefFor = (path) => {
    if (!demo) return path;
    const [pathname, query] = path.split("?");
    const base = pathname === "/dashboard" ? demo.basePath : `${demo.basePath}${pathname}`;
    return query ? `${base}?${query}` : base;
  };
  const { flags } = useFeatureFlags();
  const actionQueueCounts = useActionQueueCounts();
  // New-client registration requests count towards the same badge — they are
  // the third thing on this page waiting on the groomer.
  const pendingRegistrations = usePendingRegistrations();
  const quickActionItems = useMemo(() => getSidebarQuickActionItems(), []);

  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (!(useBottomNavLayout || isPhoneTouch)) {
      setIsDialogOpen(false);
      return;
    }

    const detectOpenDialog = () => {
      const hasVisibleDialog = !!document.querySelector(
        ".MuiDialog-root:not(.MuiModal-hidden)",
      );
      setIsDialogOpen(hasVisibleDialog);
    };

    detectOpenDialog();

    const observer = new MutationObserver(detectOpenDialog);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden"],
    });

    return () => observer.disconnect();
  }, [useBottomNavLayout, isPhoneTouch]);

  useEffect(() => {
    const node = navScrollRef.current;
    if (!node) return;

    const updateNavOverflowHint = () => {
      const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
      const showBottom = maxScrollTop > 12 && node.scrollTop < maxScrollTop - 8;
      const showTop = maxScrollTop > 12 && node.scrollTop > 8;

      setNavOverflowHint((prev) => {
        if (prev.showTop === showTop && prev.showBottom === showBottom) {
          return prev;
        }
        return { showTop, showBottom };
      });
    };

    updateNavOverflowHint();
    node.addEventListener("scroll", updateNavOverflowHint, { passive: true });
    window.addEventListener("resize", updateNavOverflowHint);

    return () => {
      node.removeEventListener("scroll", updateNavOverflowHint);
      window.removeEventListener("resize", updateNavOverflowHint);
    };
  }, [open, useBottomNavLayout, isMobile, isPhoneTouch, quickActionsCollapsed]);

  const currencySymbol = useMemo(() => {
    return (0)
      .toLocaleString("en-IE", {
        style: "currency",
        currency: currency || "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, "")
      .trim();
  }, [currency]);

  const CurrencyCircleIcon = ({ selected }) => (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        bgcolor: selected ? "var(--brand-primary, #2f4bff)" : "#6f84dd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 14,
        fontWeight: 900,
        transition: "all 0.2s ease",
      }}
    >
      {currencySymbol}
    </Box>
  );

  const groups = [
    {
      group: "Main",
      items: [
        { label: "Dashboard", icon: "🏠", path: "/dashboard" },
        {
          label: "Needs Attention",
          icon: "🔔",
          path: "/action-queue",
          tooltip:
            "Approve booking requests, chase unpaid appointments, and review new client registrations",
          badge: demo ? demo.badge || 0 : actionQueueCounts.total + pendingRegistrations.count,
          badgeNoun: "items need attention",
        },
        {
          label: "Booking History",
          icon: "📋",
          path: "/booking-history",
          tooltip: "View all past and upcoming appointments",
        },
        { label: "Clients", icon: "🐾", path: "/clients" },
        {
          label: "Services",
          icon: "✂️",
          path: "/services",
          tooltip: "Manage services & pricing",
        },
        {
          label: "Analytics",
          icon: "📈",
          path: "/analytics",
          tooltip: "Understand your business growth",
        },
        {
          label: "Stripe Payments",
          icon: <CurrencyCircleIcon />,
          path: "/payments",
          tooltip: "Daily payment operations",
        },
        {
          label: "Referrals",
          icon: "🎁",
          path: "/referrals",
          tooltip: "Share your code and track referral rewards",
        },
        {
          label: "Announcements",
          icon: "📢",
          path: "/announcements",
          tooltip: "Send updates, promotions, or price changes to your clients",
        },
        ...(flags.enableSmartRebooking ? [{
          label: "Smart Rebooking",
          icon: "🔁",
          path: "/rebooking",
          tooltip: "See overdue clients and send personalised rebooking reminders",
        }] : []),
        ...(flags.enableWaitlist ? [{
          label: "Waitlist",
          icon: "📋",
          path: "/waitlist",
          tooltip: "Manage clients waiting for a slot to open up",
        }] : []),
      ],
    },
    {
      group: "Settings",
      items: [{ label: "Settings", icon: <SettingsIcon />, path: "/settings" }],
    },
    {
      group: "Help",
      items: [
        {
          label: "Product Changelog",
          icon: <UpdateIcon />,
          path: "/changelog",
          tooltip: "See recent product changes and new features",
        },
        {
          label: isPhoneTouch ? "Support Chat" : "Contact Support",
          icon: <SupportAgentIcon />,
          path: "/support",
          // On phones the floating chat launcher is hidden (it covered the
          // page FABs), so this menu item is the way into the chat.
          onClick: isPhoneTouch ? openSupportChat : undefined,
          badge: isPhoneTouch && supportUnread > 0 ? supportUnread : 0,
          tooltip: "Get help or send feedback",
        },
      ],
    },
  ];

  const tabletTabs = [
    {
      label: "Home",
      value: "/dashboard",
      path: "/dashboard",
      icon: <HomeRoundedIcon />,
    },
    {
      label: "Clients",
      value: "/clients",
      path: "/clients",
      icon: <PetsRoundedIcon />,
    },
    {
      label: "History",
      value: "/booking-history",
      path: "/booking-history",
      icon: <ListAltRoundedIcon />,
    },
    { label: "More", value: "more", icon: <MoreHorizRoundedIcon /> },
  ];

  const tabletNavValue = useMemo(() => {
    if (logicalPath.startsWith("/dashboard")) return "/dashboard";
    if (logicalPath.startsWith("/clients")) return "/clients";
    if (logicalPath.startsWith("/booking-history"))
      return "/booking-history";
    return "more";
  }, [logicalPath]);

  const triggerQuickAdd = () => {
    setOpen(false);

    if (demo) {
      demo.onQuickAdd?.();
      return;
    }

    if (location.pathname.startsWith("/dashboard")) {
      window.dispatchEvent(new CustomEvent("groomsy:quick-add"));
      return;
    }

    const nonce = Date.now();
    navigate(`/dashboard?quickAdd=1&qa=${nonce}`);
  };

  const sidebar = (
    <Box
      sx={{
        width: useBottomNavLayout ? "100%" : SIDEBAR_WIDTH,
        height: "100%", // Inherit from wrapper (which is 100dvh or Drawer height)
        bgcolor: {
          xs: "#f6f8ff",
          sm: "linear-gradient(135deg, #f3f6ff 76%, #eaf2fe 100%)",
        },
        borderTopRightRadius: { sm: 32 },
        borderBottomRightRadius: { sm: 26 },
        boxShadow: { xs: "0 2px 14px #b8c0ff14", sm: "0 10px 36px #b0c9f71d" },
        borderRight: "1.5px solid #e1e6fb",
        display: "flex",
        flexDirection: "column",
        backdropFilter: { sm: "blur(2.5px)" },
        "@keyframes sidebarScrollHintBounce": {
          "0%, 100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(3px)" },
        },
      }}
    >
      {/* Logo & Name */}
      <Box
        sx={{
          pt: 4,
          pb: 2.25,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          mb: 0.75,
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.1,
            background:
              "radial-gradient(circle at 70% 80%, #d1e8fd 70%, #e6e8ff 100%)",
            borderRadius: "50%",
            boxShadow: "0 2px 16px #7289da2a",
          }}
        >
          <img
            src={user?.profileImg || "/logo.webp"}
            alt={user?.businessName || "Groomsy"}
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              objectFit: user?.logoFit || "cover",
              objectPosition: `${user?.logoPositionX ?? 50}% ${user?.logoPositionY ?? 50}%`,
            }}
            draggable={false}
          />
        </Box>
        <Typography
          variant="h5"
          fontWeight={900}
          letterSpacing={-0.8}
          noWrap
          sx={{ color: "#2f356a", maxWidth: "90%", textAlign: "center" }}
        >
          {user?.businessName || "Groomsy"}
        </Typography>
        {/* <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: "#6d82db", mb: 0.25, letterSpacing: ".01em" }}
        >
          Pet Pro Workspace
        </Typography> */}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Nav Groups - Scrollable Area */}
      <Box
        ref={navScrollRef}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          minHeight: 0, // Critical for flex scrolling
          position: "relative",
          // Custom scrollbar
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "transparent",
            borderRadius: "4px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            background: "#dbeafe",
          },
        }}
      >
        {navOverflowHint.showTop && (
          <Box
            aria-hidden
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              height: 18,
              pointerEvents: "none",
              background:
                "linear-gradient(180deg, rgba(246,248,255,0.94) 0%, rgba(246,248,255,0) 100%)",
            }}
          />
        )}
        {groups.map((group, idx) => (
          <React.Fragment key={group.group}>
            <Typography
              sx={{
                fontSize: 13.6,
                fontWeight: 900,
                color: "#aab6e6",
                letterSpacing: 0.6,
                textTransform: "uppercase",
                px: 3,
                pt: idx === 0 ? 0.5 : 1.6,
                pb: 0.5,
              }}
            >
              {group.group}
            </Typography>
            <List disablePadding sx={{ px: 0.6 }}>
              {group.items.map((item) => {
                const selected = logicalPath === item.path;
                const iconIsEmoji = typeof item.icon === "string";
                return (
                  <Tooltip
                    title={item.tooltip || ""}
                    arrow
                    placement="right"
                    key={item.label}
                    enterDelay={260}
                    disableHoverListener={!item.tooltip}
                  >
                    <ListItem
                      selected={selected}
                      onClick={() => {
                        // Items may navigate or run an action (e.g. opening the
                        // support chat, whose launcher is hidden on phones).
                        if (item.onClick) item.onClick();
                        else navigate(hrefFor(item.path));
                        if (isMobile || useBottomNavLayout) setOpen(false);
                      }}
                      sx={{
                        my: 0.5,
                        mx: 0.25,
                        borderRadius: 2.6,
                        pl: 2.4,
                        pr: 2.5,
                        py: 1.02,
                        cursor: "pointer",
                        color: selected
                          ? "var(--brand-primary, #2f4bff)"
                          : "#3a447a",
                        bgcolor: selected
                          ? "var(--brand-primary-bg, #e9efff)"
                          : "transparent",
                        boxShadow: selected
                          ? "inset 0 0 0 1px var(--brand-primary-bg, #cfdafe)"
                          : "none",
                        transition: "all .18s cubic-bezier(.53,.21,.29,1.1)",
                        "&:hover": {
                          bgcolor: selected
                            ? "var(--brand-primary-bg, #e6ecff)"
                            : "rgba(114,137,218,0.12)",
                          boxShadow: selected
                            ? "inset 0 0 0 1px var(--brand-primary-bg, #c5d3ff)"
                            : "0 2px 10px #7289da12",
                        },
                      }}
                      aria-current={selected ? "page" : undefined}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: selected
                            ? "var(--brand-primary, #2f4bff)"
                            : "#6f84dd",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {iconIsEmoji ? (
                          <EmojiIcon selected={selected}>{item.icon}</EmojiIcon>
                        ) : (
                          React.cloneElement(item.icon, { selected })
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          ".MuiTypography-root": {
                            fontWeight: selected ? 900 : 700,
                            letterSpacing: "-0.35px",
                            fontSize: 16.8,
                          },
                        }}
                      />
                      {item.badge > 0 && (
                        <Box
                          aria-label={`${item.label}: ${item.badge} ${
                            item.badgeNoun || "unread"
                          }`}
                          sx={{
                            minWidth: 20,
                            height: 20,
                            px: 0.6,
                            borderRadius: 999,
                            bgcolor: "#e53935",
                            color: "#fff",
                            fontSize: 11.5,
                            fontWeight: 800,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {item.badge > 99 ? "99+" : item.badge}
                        </Box>
                      )}
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
            {idx < groups.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
        {navOverflowHint.showBottom && (
          <Box
            aria-hidden
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 3,
              px: 2,
              pb: 0.8,
              pt: 3.2,
              mt: "auto",
              pointerEvents: "none",
              background:
                "linear-gradient(0deg, rgba(246,248,255,0.98) 35%, rgba(246,248,255,0) 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.2,
            }}
          >
            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize: 19,
                color: "#7b8fd2",
                animation: "sidebarScrollHintBounce 1.35s ease-in-out infinite",
              }}
            />
            <Typography
              sx={{
                fontSize: 10.8,
                fontWeight: 800,
                letterSpacing: 0.35,
                textTransform: "uppercase",
                color: "#7b8fd2",
              }}
            >
              Scroll for more
            </Typography>
          </Box>
        )}
      </Box>

      {/* User / Business Info & Actions */}
      <Box
        sx={{
          width: "100%",
          px: 2,
          pb: "calc(24px + env(safe-area-inset-bottom))", // Ensure safe area + padding
          pt: 2,
          borderTop: "1px solid #e1e6fb",
          bgcolor: "#f9fbff",
          zIndex: 2,
          boxShadow: "0 -4px 20px rgba(0,0,0,0.03)", // Subtle lift
          maxHeight: "42dvh",
          overflowY: "auto",
          minHeight: 0,

          // Subtle scrollbar for this section when expanded on shorter screens.
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "transparent",
            borderRadius: "4px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            background: "#dbeafe",
          },
        }}
      >
        {/* Plan badge */}
        <Box sx={{ mb: 1 }}>
          {(() => {
            const badge = planBadge(user);
            return (
              <Tooltip title={badge.tooltip} arrow>
                <Chip
                  label={badge.label}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    letterSpacing: 0.2,
                    color: badge.color,
                    bgcolor: badge.bg,
                    borderRadius: 1.5,
                    ".MuiChip-label": { px: 1 },
                  }}
                />
              </Tooltip>
            );
          })()}
        </Box>

        {/* Quick Actions */}
        <Box
          sx={{
            mt: 1.25,
            p: 1.25,
            borderRadius: 3,
            border: "1px solid #e4e9f5",
            bgcolor: "#fbfcfe",
          }}
        >
          <ButtonBase
            onClick={() => setQuickActionsCollapsed((v) => !v)}
            sx={{
              width: "100%",
              borderRadius: 2,
              px: 0.6,
              py: 0.35,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              textAlign: "left",
              "&:hover": { bgcolor: "rgba(41,81,163,.05)" },
            }}
            aria-expanded={!quickActionsCollapsed}
            aria-controls="sidebar-share-access-body"
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: "#8791ab",
                textTransform: "uppercase",
              }}
            >
              Share & Access
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color: "#8791ab",
              }}
            >
              {quickActionsCollapsed ? (
                <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />
              ) : (
                <ExpandLessRoundedIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
          </ButtonBase>

          <Collapse in={!quickActionsCollapsed} timeout={180}>
            <Box id="sidebar-share-access-body">
              <Box
                sx={{
                  display: "grid",
                  gap: 0.75,
                  mt: 1,
                }}
              >
                {quickActionItems.map((item) => {
                  const ActionIcon = item.Icon;

                  return (
                    <ButtonBase
                      key={item.key}
                      onClick={async () => {
                        if (demo) {
                          // The booking link opens the customer-facing demo page.
                          if (item.key === "booking") {
                            navigate(`${demo.basePath}/book`);
                            return;
                          }
                          setSnackbar({
                            open: true,
                            message:
                              "In your account this copies your own link — sign up to get it.",
                            severity: "info",
                          });
                          return;
                        }
                        const url = item.buildUrl(user);
                        try {
                          await navigator.clipboard.writeText(url);
                          setSnackbar({
                            open: true,
                            message: item.successMessage,
                            severity: "success",
                          });
                        } catch {
                          setSnackbar({
                            open: true,
                            message: item.errorMessage,
                            severity: "error",
                          });
                        }
                      }}
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        borderRadius: 2.5,
                        px: 1.1,
                        py: 0.95,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.1,
                        textAlign: "left",
                        bgcolor: "#ffffff",
                        border: "1px solid #e6eaf3",
                        transition: "background-color .15s, border-color .15s",
                        "&:hover": {
                          bgcolor: "#f6f8fd",
                          borderColor: "#c9d4ec",
                        },
                        "&:hover .copy-affordance": {
                          color: "#2951a3",
                          bgcolor: "rgba(41,81,163,.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.75,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(41,81,163,.08)",
                          color: "#2951a3",
                          flexShrink: 0,
                        }}
                      >
                        <ActionIcon sx={{ fontSize: 17 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 12.8,
                            color: "#1f2a44",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 11.2,
                            color: "#7b869f",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                      <Box
                        className="copy-affordance"
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          color: "#9aa4bd",
                          bgcolor: "rgba(41,81,163,.05)",
                          transition: "color .15s, background-color .15s",
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14.5 }} />
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>

              <Divider sx={{ my: 1.1, borderColor: "#e6eaf3" }} />

              <Button
                fullWidth
                variant="text"
                disableRipple
                startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                onClick={() => (demo ? demo.onExit() : signOutAndClearCache())}
                sx={{
                  minHeight: 38,
                  borderRadius: 2,
                  textTransform: "none",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 12.3,
                  color: "#8791ab",
                  "&:hover": {
                    color: "#a11d33",
                    bgcolor: "rgba(161,29,51,.06)",
                  },
                  "& .MuiButton-startIcon": { mr: 0.6 },
                }}
              >
                {demo ? "Exit demo" : "Log Out"}
              </Button>
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "#f6f8ff" }}>
      {useBottomNavLayout ? (
        <>
          <Box
            sx={{
              width: "100%",
              minHeight: `calc(100dvh - ${APP_BAR_HEIGHT_DESKTOP}px)`,
              pb: isDialogOpen
                ? "max(16px, env(safe-area-inset-bottom))"
                : "calc(84px + env(safe-area-inset-bottom))",
            }}
          >
            {children}
          </Box>

          <Drawer
            anchor="bottom"
            open={open}
            onClose={() => setOpen(false)}
            sx={{ zIndex: 1304 }}
            PaperProps={{
              sx: getTabletMoreDrawerPaperStyles(),
            }}
          >
            {sidebar}
          </Drawer>

          <Box
            sx={{
              display: isDialogOpen ? "none" : "flex",
              flexDirection: "column",
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1302,
              borderTop: "1px solid rgba(15,23,42,0.07)",
              boxShadow: "0 -4px 24px rgba(15,23,42,0.06)",
              bgcolor: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(24px) saturate(200%)",
              pb: "env(safe-area-inset-bottom)",
            }}
          >
            {/* Quick Add FAB */}
            <Fab
              aria-label="Quick add"
              onClick={(e) => setQuickAddAnchorEl(e.currentTarget)}
              sx={{
                position: "absolute",
                left: "50%",
                top: -26,
                transform: "translateX(-50%)",
                width: 56,
                height: 56,
                background: "linear-gradient(145deg, #3CC6A0 0%, #29a889 100%)",
                boxShadow:
                  "0 4px 16px rgba(60,198,160,0.42), 0 2px 4px rgba(60,198,160,0.2)",
                border: "3px solid rgba(255,255,255,0.97)",
                zIndex: 2,
                transition: "all 0.2s cubic-bezier(.22,1,.36,1)",
                "&:hover": {
                  background: "linear-gradient(145deg, #4dd4b0 0%, #34b898 100%)",
                  boxShadow: "0 8px 24px rgba(60,198,160,0.48)",
                  transform: "translateX(-50%) translateY(-2px)",
                },
                "&:active": {
                  transform: "translateX(-50%) scale(0.92)",
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 26, color: "#fff" }} />
            </Fab>
            <Menu
              anchorEl={quickAddAnchorEl}
              open={Boolean(quickAddAnchorEl)}
              onClose={() => setQuickAddAnchorEl(null)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              transformOrigin={{ vertical: "bottom", horizontal: "center" }}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  border: "1px solid #e8edf5",
                  boxShadow: "0 8px 32px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)",
                  minWidth: 200,
                  mb: 1.5,
                  overflow: "hidden",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  setQuickAddAnchorEl(null);
                  triggerQuickAdd();
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  gap: 1.5,
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: "#0f172a",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "rgba(60,198,160,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CalendarMonthIcon sx={{ color: "#3CC6A0", fontSize: 18 }} />
                </Box>
                New appointment
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setQuickAddAnchorEl(null);
                  navigate(hrefFor("/clients?add=1"));
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  gap: 1.5,
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: "#0f172a",
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    bgcolor: "rgba(91,124,250,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonAddAlt1Icon sx={{ color: "#5b7cfa", fontSize: 18 }} />
                </Box>
                New client
              </MenuItem>
            </Menu>

            {/* Tab row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: 68,
                px: 1,
              }}
            >
              {/* Render all 4 tabs with FAB spacer in the middle */}
              {[tabletTabs[0], tabletTabs[1], null, tabletTabs[2], tabletTabs[3]].map((tab, idx) => {
                if (tab === null) {
                  return <Box key="fab-spacer" sx={{ flex: 1.2, flexShrink: 0 }} />;
                }
                const isSelected = tabletNavValue === tab.value;
                return (
                  <ButtonBase
                    key={tab.value}
                    onClick={() => {
                      if (tab.value === "more") {
                        setOpen(true);
                        return;
                      }
                      navigate(hrefFor(tab.path));
                    }}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.4,
                      height: "100%",
                      borderRadius: "12px",
                      WebkitTapHighlightColor: "transparent",
                      transition: "opacity 0.15s",
                      "&:active": { opacity: 0.7 },
                    }}
                  >
                    {/* Icon with active indicator dot */}
                    <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {React.cloneElement(tab.icon, {
                        sx: {
                          fontSize: 22,
                          color: isSelected
                            ? "var(--brand-primary, #2951A3)"
                            : "#b0bcd6",
                          transition: "color 0.18s ease",
                          display: "block",
                        },
                      })}
                      {/* Active dot beneath icon */}
                      {isSelected && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -4,
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: 4,
                            height: 4,
                            borderRadius: "50%",
                            bgcolor: "var(--brand-primary, #2951A3)",
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 10.5,
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected
                          ? "var(--brand-primary, #2951A3)"
                          : "#b0bcd6",
                        letterSpacing: "-0.1px",
                        transition: "color 0.18s ease",
                        lineHeight: 1,
                        userSelect: "none",
                        mt: 0.75,
                      }}
                    >
                      {tab.label}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
        </>
      ) : isPhoneTouch || isMobile ? (
        <>
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              position: "fixed",
              top: 16,
              left: 16,
              zIndex: 1300,
              bgcolor: "#fff",
              border: "1px solid #e1e6fb",
              boxShadow: "0 2px 8px #b8c0ff12",
              "&:hover": { bgcolor: "#f6f8fc" },
            }}
            size="large"
            aria-label="Open menu"
          >
            <MenuIcon sx={{ color: "#6f84dd", fontSize: 32 }} />
          </IconButton>
          <Drawer
            open={open}
            onClose={() => setOpen(false)}
            PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}
          >
            {sidebar}
          </Drawer>
          <Box sx={{ width: "100%" }}>{children}</Box>
        </>
      ) : (
        <>
          {/* Desktop: Fixed Sidebar */}
          <Box
            sx={{
              width: SIDEBAR_WIDTH,
              height: `calc(100dvh - ${APP_BAR_HEIGHT_DESKTOP}px)`,
              position: "fixed", // Fixed to viewport
              left: 0,
              top: `${APP_BAR_HEIGHT_DESKTOP}px`,
              zIndex: 1200,
              overflow: "hidden",
              borderRight: "1px solid #e1e6fb", // Border on the fixed container
              bgcolor: "#fff", // Ensure partial opacity bg from sidebar doesn't show transparently over nothing

              // Hide scrollbar
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {sidebar}
          </Box>

          {/* Main Content - Pushed by Margin */}
          <Box
            sx={{
              // Push content to right
              marginLeft: `${SIDEBAR_WIDTH}px`,
              minHeight: `calc(100dvh - ${APP_BAR_HEIGHT_DESKTOP}px)`,
              bgcolor: "#f6f8ff",
              pt: { xs: 0, sm: 0 },
              width: `calc(100% - ${SIDEBAR_WIDTH}px)`, // Ensure explicit width
              flexGrow: 1,
            }}
          >
            {children}
          </Box>
        </>
      )}
    </Box>
  );
}
